import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2, Users, BookOpen, GraduationCap, BarChart3, Plus,
  Settings, Shield, Eye, Edit, Trash2, UserPlus, Copy, Check,
  Globe, Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'co_admin', label: 'Co-Administrateur' },
  { value: 'rector', label: 'Recteur' },
  { value: 'vice_rector_academic', label: 'Vice-Recteur Academique' },
  { value: 'sg_academic', label: 'SG Academique' },
  { value: 'sg_admin', label: 'SG Administratif' },
  { value: 'sg_research', label: 'SG Recherche' },
  { value: 'budget_director', label: 'Admin. Budget' },
  { value: 'dean', label: 'Doyen' },
  { value: 'vice_dean', label: 'Vice-Doyen' },
  { value: 'faculty_secretary', label: 'Secretaire Acad. Faculte' },
  { value: 'department_head', label: 'Chef Departement' },
  { value: 'section_head', label: 'Chef Section' },
  { value: 'dept_secretary', label: 'Secretaire Departement' },
  { value: 'teacher', label: 'Enseignant' },
  { value: 'visiting_teacher', label: 'Enseignant Visiteur' },
  { value: 'admin_staff', label: 'Personnel Admin.' },
  { value: 'academic_secretary', label: 'Secretaire Acad.' },
  { value: 'registrar', label: 'Charge Inscriptions' },
  { value: 'cashier', label: 'Caissier / Comptable' },
  { value: 'librarian', label: 'Bibliothecaire' },
  { value: 'it_admin', label: 'Informaticien' },
  { value: 'apparitor', label: 'Appariteur' },
  { value: 'protocol_chief', label: 'Chef Protocole' },
  { value: 'receptionist', label: 'Huissier / Accueil' },
  { value: 'student', label: 'Etudiant' },
  { value: 'driver', label: 'Chauffeur' },
];

export const SuperAdminPage = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateUni, setShowCreateUni] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showGenTeacherIds, setShowGenTeacherIds] = useState(false);
  const [teacherIds, setTeacherIds] = useState([]);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/stats/global`);
      setStats(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadTeacherIds = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/teacher-ids`);
      setTeacherIds(res.data);
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="animate-pulse space-y-4">{[1,2,3].map(i => <Card key={i}><CardContent className="p-6"><div className="h-20 bg-gray-100 rounded"/></CardContent></Card>)}</div>;

  const l = language === 'fr' ? {
    title: 'Panel Super Administrateur',
    subtitle: 'Vue globale de la plateforme JoSmart Uni',
    overview: 'Vue globale',
    universities: 'Universites',
    users: 'Utilisateurs',
    tools: 'Outils Admin',
    totalUni: 'Universites',
    activeUni: 'Actives',
    totalUsers: 'Utilisateurs',
    totalStudents: 'Etudiants',
    totalTeachers: 'Enseignants',
    totalCourses: 'Cours',
    totalGroups: 'Groupes',
    createUni: 'Creer une universite',
    createUser: 'Creer un utilisateur',
    genTeacherIds: 'Generer des IDs enseignants',
    manageTeacherIds: 'Gerer les IDs enseignants',
    roleDistribution: 'Distribution des roles',
    perUni: 'Par universite',
  } : {
    title: 'Super Admin Panel',
    subtitle: 'Global platform overview',
    overview: 'Overview',
    universities: 'Universities',
    users: 'Users',
    tools: 'Admin Tools',
    totalUni: 'Universities',
    activeUni: 'Active',
    totalUsers: 'Users',
    totalStudents: 'Students',
    totalTeachers: 'Teachers',
    totalCourses: 'Courses',
    totalGroups: 'Groups',
    createUni: 'Create university',
    createUser: 'Create user',
    genTeacherIds: 'Generate teacher IDs',
    manageTeacherIds: 'Manage teacher IDs',
    roleDistribution: 'Role distribution',
    perUni: 'Per university',
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="super-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-title">{l.title}</h1>
          <p className="text-sm text-gray-500">{l.subtitle}</p>
        </div>
        <Badge className="bg-[#0F2C59] text-white"><Shield className="w-3 h-3 mr-1" /> Super Admin</Badge>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" className="gap-1"><BarChart3 className="w-4 h-4" />{l.overview}</TabsTrigger>
          <TabsTrigger value="universities" className="gap-1"><Building2 className="w-4 h-4" />{l.universities}</TabsTrigger>
          <TabsTrigger value="coadmins" className="gap-1"><Shield className="w-4 h-4" />Co-Admins</TabsTrigger>
          <TabsTrigger value="tools" className="gap-1"><Settings className="w-4 h-4" />{l.tools}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Building2} label={l.totalUni} value={stats?.total_universities} color="blue" />
            <StatCard icon={Users} label={l.totalUsers} value={stats?.total_users} color="purple" />
            <StatCard icon={GraduationCap} label={l.totalStudents} value={stats?.total_students} color="green" />
            <StatCard icon={BookOpen} label={l.totalCourses} value={stats?.total_courses} color="amber" />
          </div>

          {/* Role Distribution */}
          <Card>
            <CardHeader><CardTitle className="text-base">{l.roleDistribution}</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {stats?.role_distribution && Object.entries(stats.role_distribution).map(([role, count]) => (
                  count > 0 && (
                    <div key={role} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-xs text-gray-600 truncate">{ROLE_OPTIONS.find(r => r.value === role)?.label || role}</span>
                      <Badge variant="secondary" className="text-xs">{count}</Badge>
                    </div>
                  )
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Universities Tab */}
        <TabsContent value="universities" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowCreateUni(true)} className="gap-2" data-testid="create-uni-button">
              <Plus className="w-4 h-4" /> {l.createUni}
            </Button>
          </div>
          <div className="space-y-3">
            {stats?.universities?.map(uni => (
              <Card key={uni.id} data-testid={`uni-card-${uni.code}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{uni.name}</h3>
                        <Badge variant="outline" className="font-mono text-xs">{uni.code}</Badge>
                      </div>
                      <p className="text-xs text-gray-500">{uni.city} | {uni.total_users} utilisateurs | {uni.total_students} etudiants | {uni.total_courses} cours</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={uni.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {uni.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Co-Admins Tab */}
        <TabsContent value="coadmins" className="mt-4">
          <CoAdminManager language={language} />
        </TabsContent>

        {/* Tools Tab */}
        <TabsContent value="tools" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="card-hover cursor-pointer" onClick={() => setShowCreateUser(true)} data-testid="tool-create-user">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <UserPlus className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold">{l.createUser}</h3>
                  <p className="text-xs text-gray-500">{language === 'fr' ? 'Creer un utilisateur avec n\'importe quel role' : 'Create a user with any role'}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover cursor-pointer" onClick={() => { setShowGenTeacherIds(true); loadTeacherIds(); }} data-testid="tool-teacher-ids">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold">{l.genTeacherIds}</h3>
                  <p className="text-xs text-gray-500">{language === 'fr' ? 'Generer et gerer les IDs d\'inscription enseignants' : 'Generate and manage teacher enrollment IDs'}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create University Dialog */}
      {showCreateUni && <CreateUniversityDialog onClose={() => { setShowCreateUni(false); loadStats(); }} language={language} />}
      {showCreateUser && <CreateUserDialog onClose={() => { setShowCreateUser(false); loadStats(); }} language={language} universities={stats?.universities || []} />}
      {showGenTeacherIds && <TeacherIdsDialog onClose={() => setShowGenTeacherIds(false)} language={language} teacherIds={teacherIds} onRefresh={loadTeacherIds} universities={stats?.universities || []} />}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = { blue: 'bg-blue-100 text-blue-600', purple: 'bg-purple-100 text-purple-600', green: 'bg-green-100 text-green-600', amber: 'bg-amber-100 text-amber-600' };
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value ?? 0}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Create University Dialog
const CreateUniversityDialog = ({ onClose, language }) => {
  const [form, setForm] = useState({ name: '', type: 'university', city: '', province: '', address: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/universities`, form);
      toast.success(language === 'fr' ? `Universite creee ! Code: ${res.data.code}` : `University created! Code: ${res.data.code}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{language === 'fr' ? 'Creer une universite' : 'Create university'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>{language === 'fr' ? 'Nom' : 'Name'}</Label><Input value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))} data-testid="uni-name-input" /></div>
          <div><Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm(p => ({...p, type: v}))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="university">Universite</SelectItem>
                <SelectItem value="institute">Institut Superieur</SelectItem>
                <SelectItem value="school">Ecole Superieure</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{language === 'fr' ? 'Ville' : 'City'}</Label><Input value={form.city} onChange={(e) => setForm(p => ({...p, city: e.target.value}))} /></div>
            <div><Label>Province</Label><Input value={form.province} onChange={(e) => setForm(p => ({...p, province: e.target.value}))} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>{language === 'fr' ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={handleSubmit} disabled={loading || !form.name} data-testid="submit-create-uni">{language === 'fr' ? 'Creer' : 'Create'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Create User Dialog
const CreateUserDialog = ({ onClose, language, universities }) => {
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', phone: '', role: 'teacher', university_id: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/create-user`, form);
      toast.success(language === 'fr' ? 'Utilisateur cree' : 'User created');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{language === 'fr' ? 'Creer un utilisateur' : 'Create user'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{language === 'fr' ? 'Prenom' : 'First name'}</Label><Input value={form.first_name} onChange={(e) => setForm(p => ({...p, first_name: e.target.value}))} data-testid="create-user-firstname" /></div>
            <div><Label>{language === 'fr' ? 'Nom' : 'Last name'}</Label><Input value={form.last_name} onChange={(e) => setForm(p => ({...p, last_name: e.target.value}))} /></div>
          </div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} data-testid="create-user-email" /></div>
          <div><Label>{language === 'fr' ? 'Mot de passe' : 'Password'}</Label><Input type="password" value={form.password} onChange={(e) => setForm(p => ({...p, password: e.target.value}))} /></div>
          <div><Label>{language === 'fr' ? 'Role' : 'Role'}</Label>
            <Select value={form.role} onValueChange={(v) => setForm(p => ({...p, role: v}))}>
              <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>{language === 'fr' ? 'Universite' : 'University'}</Label>
            <Select value={form.university_id} onValueChange={(v) => setForm(p => ({...p, university_id: v}))}>
              <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Choisir...' : 'Select...'} /></SelectTrigger>
              <SelectContent>
                {universities.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>{language === 'fr' ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={handleSubmit} disabled={loading || !form.email || !form.password || !form.first_name} data-testid="submit-create-user">{language === 'fr' ? 'Creer' : 'Create'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Teacher IDs Dialog
const TeacherIdsDialog = ({ onClose, language, teacherIds, onRefresh, universities }) => {
  const [count, setCount] = useState(5);
  const [uniId, setUniId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const handleGenerate = async () => {
    if (!uniId) { toast.error(language === 'fr' ? 'Selectionnez une universite' : 'Select a university'); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/generate-teacher-ids`, { count, university_id: uniId });
      toast.success(res.data.message);
      onRefresh();
    } catch (err) { toast.error('Erreur'); }
    finally { setGenerating(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedId(code);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh]">
        <DialogHeader><DialogTitle>{language === 'fr' ? 'IDs Enseignants' : 'Teacher IDs'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Generate new */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-3">
            <p className="font-medium text-sm">{language === 'fr' ? 'Generer de nouveaux IDs' : 'Generate new IDs'}</p>
            <Select value={uniId} onValueChange={setUniId}>
              <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Universite...' : 'University...'} /></SelectTrigger>
              <SelectContent>
                {universities.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input type="number" value={count} onChange={(e) => setCount(parseInt(e.target.value) || 1)} min={1} max={100} className="w-24" />
              <Button onClick={handleGenerate} disabled={generating} className="flex-1" data-testid="generate-teacher-ids-btn">
                {generating ? '...' : language === 'fr' ? `Generer ${count} ID(s)` : `Generate ${count} ID(s)`}
              </Button>
            </div>
          </div>

          {/* List existing */}
          <ScrollArea className="max-h-64">
            <div className="space-y-2">
              {teacherIds.map(tid => (
                <div key={tid.id} className="flex items-center justify-between p-2 border rounded-lg text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant={tid.is_used ? 'secondary' : 'default'} className={`font-mono text-xs ${tid.is_used ? 'bg-gray-100' : 'bg-green-100 text-green-700'}`}>
                      {tid.code}
                    </Badge>
                    {tid.is_used && <span className="text-xs text-gray-500">{tid.used_by_email}</span>}
                  </div>
                  {!tid.is_used && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyCode(tid.code)}>
                      {copiedId === tid.code ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  )}
                </div>
              ))}
              {teacherIds.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">{language === 'fr' ? 'Aucun ID genere' : 'No IDs generated'}</p>
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SuperAdminPage;

// Co-Admin Manager Component
const CoAdminManager = ({ language }) => {
  const [coAdmins, setCoAdmins] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', permissions: [] });
  const [creating, setCreating] = useState(false);

  const PERMISSION_OPTIONS = [
    { value: 'universities', label: 'Universites' },
    { value: 'users', label: 'Utilisateurs' },
    { value: 'payments', label: 'Paiements' },
    { value: 'reports', label: 'Rapports' },
    { value: 'notifications', label: 'Notifications' },
    { value: 'schedule', label: 'Calendrier' },
    { value: 'deliberations', label: 'Deliberations' },
  ];

  useEffect(() => { fetchCoAdmins(); }, []);

  const fetchCoAdmins = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/co-admins`);
      setCoAdmins(res.data);
    } catch {} finally { setLoading(false); }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/co-admins`, form);
      toast.success('Co-administrateur cree');
      setShowCreate(false);
      setForm({ email: '', password: '', first_name: '', last_name: '', permissions: [] });
      fetchCoAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  const handleRevoke = async (userId) => {
    try {
      await axios.delete(`${API_URL}/api/co-admins/${userId}`);
      toast.success('Co-admin revoque');
      fetchCoAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const togglePermission = (perm) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const l = language === 'fr';

  return (
    <div className="space-y-4" data-testid="co-admin-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{l ? 'Gestion des Co-Administrateurs' : 'Co-Admin Management'}</h3>
          <p className="text-xs text-gray-500">{l ? 'Nommer et gerer les co-administrateurs de la plateforme' : 'Assign and manage platform co-administrators'}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="add-co-admin-btn">
          <UserPlus className="w-4 h-4 mr-2" />{l ? 'Ajouter' : 'Add'}
        </Button>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">{[1,2].map(i => <div key={i} className="h-20 bg-gray-100 rounded" />)}</div>
      ) : coAdmins.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">
          <Shield className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>{l ? 'Aucun co-administrateur nomme' : 'No co-admins assigned'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {coAdmins.map(admin => (
            <Card key={admin.id} data-testid={`co-admin-${admin.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0F2C59] text-white flex items-center justify-center text-sm font-bold">
                    {(admin.first_name?.[0] || '').toUpperCase()}{(admin.last_name?.[0] || '').toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{admin.first_name} {admin.last_name}</p>
                    <p className="text-xs text-gray-500">{admin.email}</p>
                    {admin.permissions?.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {admin.permissions.map(p => (
                          <Badge key={p} variant="outline" className="text-[10px] py-0">{p}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleRevoke(admin.id)} data-testid={`revoke-${admin.id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" />{l ? 'Revoquer' : 'Revoke'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Co-Admin Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{l ? 'Nouveau Co-Administrateur' : 'New Co-Admin'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{l ? 'Prenom' : 'First name'}</Label>
                <Input value={form.first_name} onChange={e => setForm(p => ({...p, first_name: e.target.value}))} required data-testid="co-admin-first-name" />
              </div>
              <div><Label>{l ? 'Nom' : 'Last name'}</Label>
                <Input value={form.last_name} onChange={e => setForm(p => ({...p, last_name: e.target.value}))} />
              </div>
            </div>
            <div><Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} required data-testid="co-admin-email" />
            </div>
            <div><Label>{l ? 'Mot de passe' : 'Password'}</Label>
              <Input type="password" value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} required data-testid="co-admin-password" />
            </div>
            <div>
              <Label>{l ? 'Permissions' : 'Permissions'}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {PERMISSION_OPTIONS.map(opt => (
                  <button key={opt.value} type="button"
                    className={`px-3 py-1.5 rounded-md text-xs border transition-all ${
                      form.permissions.includes(opt.value)
                        ? 'bg-[#0F2C59] text-white border-[#0F2C59]'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => togglePermission(opt.value)} data-testid={`perm-${opt.value}`}
                  >{opt.label}</button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>{l ? 'Annuler' : 'Cancel'}</Button>
              <Button onClick={handleCreate} disabled={creating} data-testid="co-admin-submit">
                {creating ? (l ? 'Creation...' : 'Creating...') : (l ? 'Creer' : 'Create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
